import { useVitals } from '@hook'
import { useAppSelector } from '@store/hooks'
import { VitalDataMock } from '@type/vital.type'
import { FormButton, FormInputField, ModalHeader } from '@ui'
import { getError, getVitalValueColor, getVitalValueInputClassName, isBmiVital, showToast } from '@util'
import { Box, Layer } from 'grommet'
import { useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'

type VitalInput = {
  id?: string | number
  vital_type: string
  value: string
  unit: string
  previousValue?: string
}

type FormValues = {
  vitals: VitalInput[]
}

type DefaultVitalAttributes = Partial<VitalInput> & {
  captured_date?: string
  time?: string
  vitals?: VitalInput[]
  patient_id?: string | number
  intake_id?: string | number
}

type Props = {
  onClose: () => void
  defaultData?: VitalDataMock | DefaultVitalAttributes
  id?: string
}

const normalizeApiId = (value: string | number) => {
  const numericValue = Number(value)
  return Number.isNaN(numericValue) ? value : numericValue
}

const EditNurseVital = ({ onClose, defaultData }: Props) => {
  const { patientId } = useParams<string>()
  const { intakeId: routeIntakeId } = useParams<string>()
  const [searchParams] = useSearchParams()
  const intakeId = searchParams.get('intake') || routeIntakeId
  const { vitals: vitalTypes } = useAppSelector(state => state.config)
  const { correctVitals } = useVitals({ patientId: patientId || '' })
  const defaultAttributes = useMemo(() => {
    return ('attributes' in (defaultData || {}) ? (defaultData as VitalDataMock)?.attributes : defaultData) as
      | DefaultVitalAttributes
      | undefined
  }, [defaultData])

  const initialVitals = useMemo(() => {
    const savedVitals = Array.isArray(defaultAttributes?.vitals)
      ? defaultAttributes.vitals
      : defaultAttributes?.vital_type
        ? [defaultAttributes]
        : []

    return (
      vitalTypes?.map(vital => {
        const savedVital = savedVitals.find(item => item.vital_type === vital.name)

        return {
          id: savedVital?.id,
          unit: savedVital?.unit || vital.unit || '',
          previousValue: savedVital?.value?.toString() || '',
          value: savedVital?.value?.toString() || '',
          vital_type: vital.name
        }
      }) || []
    )
  }, [defaultAttributes, vitalTypes])

  const methods = useForm<FormValues>({
    defaultValues: { vitals: initialVitals }
  })
  const { control, handleSubmit, reset, setValue, watch } = methods

  useEffect(() => {
    reset({ vitals: initialVitals })
  }, [initialVitals, reset])

  // Locate BMI / height / weight rows by vital_type.
  const bmiIndex = useMemo(() => initialVitals.findIndex(v => isBmiVital(v.vital_type)), [initialVitals])
  const heightIndex = useMemo(
    () => initialVitals.findIndex(v => v.vital_type.toLowerCase() === 'height'),
    [initialVitals]
  )
  const weightIndex = useMemo(
    () => initialVitals.findIndex(v => v.vital_type.toLowerCase() === 'weight'),
    [initialVitals]
  )

  // Watch the height and weight fields individually so BMI recalculates the
  // instant either changes.
  const height = watch(`vitals.${heightIndex}.value`)
  const weight = watch(`vitals.${weightIndex}.value`)
  const heightUnit = watch(`vitals.${heightIndex}.unit`)
  const bmi = watch(`vitals.${bmiIndex}.value`)

  useEffect(() => {
    if (bmiIndex < 0) return

    const h = Number(height)
    const w = Number(weight)

    if (!height || !weight || !h || !w) {
      setValue(`vitals.${bmiIndex}.value`, '')
      return
    }

    const heightInMeters = heightUnit === 'mm' ? h / 1000 : heightUnit === 'cm' ? h / 100 : h
    const calculated = w / (heightInMeters * heightInMeters)
    setValue(`vitals.${bmiIndex}.value`, calculated.toFixed(1))
  }, [height, weight, bmiIndex, setValue])

  const onSubmit = async (data: FormValues) => {
    // Include any vitals where a value was entered — allow creating new vitals
    const vitalsToSubmit = data.vitals.filter(
      vital => vital.value !== '' && vital.value !== null && vital.value !== undefined
    )

    if (vitalsToSubmit.length === 0) {
      showToast('Please enter at least one vital value')
      return
    }

    const patientIdValue = defaultAttributes?.patient_id || patientId
    const intakeIdValue = defaultAttributes?.intake_id || intakeId

    if (!patientIdValue || !intakeIdValue) {
      showToast('Unable to correct vitals')
      return
    }

    const vitalsArray = vitalsToSubmit.map(vital => {
      const base = {
        unit: vital.unit,
        value: String(vital.value),
        vital_type: vital.vital_type
      } as any

      // Only include id when present (corrections); omit for new vitals
      if (vital.id !== undefined && vital.id !== null && vital.id !== '') {
        base.id = normalizeApiId(vital.id)
      }

      return base
    })

    const payload = {
      data: {
        attributes: {
          intake_id: normalizeApiId(intakeIdValue),
          patient_id: normalizeApiId(patientIdValue),
          vitals: vitalsArray
        },
        type: 'vital_sign'
      }
    }

    try {
      await correctVitals(payload)
      showToast('Vitals corrected successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Layer
        margin={{ vertical: '8px' }}
        modal
        onClickOutside={onClose}
        onEsc={onClose}
        position="center"
        style={{ borderRadius: '15px' }}
      >
        <form className="flex flex-col overflow-auto w-full min-w-0 max-w-[700px]" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader onClose={onClose} title="Correct Vital Signs" />
          <Box overflow="hidden" pad="medium">
            <div
              className="mb-4"
              style={{
                border: '1px solid #ccc',
                borderRadius: '8px',
                marginBottom: '1rem',
                overflow: 'auto',
                width: '100%'
              }}
            >
              <table
                className="w-full !p-0"
                style={{
                  borderCollapse: 'collapse',
                  overflow: 'auto',
                  width: '100%'
                }}
              >
                <thead>
                  <tr>
                    <th
                      className="text-left py-2 bg-[#F5F5F5] px-4"
                      style={{
                        borderBottom: '1px solid #ccc'
                      }}
                    >
                      Vitals
                    </th>
                    <th
                      className="text-left py-2 bg-[#F5F5F5] px-4"
                      style={{
                        borderBottom: '1px solid #ccc'
                      }}
                    >
                      Current Value
                    </th>
                    <th
                      className="text-left py-2 bg-[#F5F5F5] px-4"
                      style={{
                        borderBottom: '1px solid #ccc'
                      }}
                    >
                      Corrected Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {initialVitals?.map((vital, idx) => {
                    const isBmi = isBmiVital(vital.vital_type)

                    return (
                      <tr key={vital.vital_type}>
                        <td
                          className="py-2 px-4"
                          style={{
                            borderBottom: '1px solid #ccc',
                            textTransform: 'capitalize'
                          }}
                        >
                          {vital.vital_type.replace(/_/g, ' ')}
                        </td>
                        <td
                          className="py-2 px-4"
                          style={{
                            borderBottom: '1px solid #ccc'
                          }}
                        >
                          <span style={{ color: '#8E8E8E' }}>
                            {vital.previousValue || '-'} {vital.unit}
                          </span>
                        </td>
                        <td
                          className="py-2 px-4"
                          style={{
                            borderBottom: '1px solid #ccc'
                          }}
                        >
                          {isBmi ? (
                            // BMI is derived — render a plain controlled input
                            // so the recalculated value shows immediately.
                            <div className="relative flex w-full items-center" style={{ minWidth: 100 }}>
                              <input
                                className={
                                  'w-full bg-transparent px-4 h-11 text-sm outline-none border-0' +
                                  ' ' +
                                  getVitalValueInputClassName(getVitalValueColor(vitalTypes, vital.vital_type, bmi))
                                }
                                readOnly
                                tabIndex={-1}
                                title="bmi"
                                value={idx === bmiIndex ? bmi : ''}
                              />
                              {vital.unit && (
                                <span className="absolute inset-y-0 right-4 flex items-center justify-center text-sm opacity-30">
                                  {vital.unit}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Controller
                              control={control}
                              name={`vitals.${idx}.value`}
                              render={({ field }) => (
                                <FormInputField
                                  {...field}
                                  inputClassName={getVitalValueInputClassName(
                                    getVitalValueColor(vitalTypes, vital.vital_type, field.value)
                                  )}
                                  label=""
                                  name={field.name}
                                  no_border
                                  placeholder="Enter value"
                                  style={{ minWidth: 100, outlineWidth: '0px' }}
                                  suffix={vital.unit}
                                  type="number"
                                />
                              )}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Box>
          <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="save" />
        </form>
      </Layer>
    </FormProvider>
  )
}

export default EditNurseVital
